/**
 * Modèle de couches agnostique du moteur de rendu (Leaflet, ex-ECharts).
 *
 * Extrait de `buildMapOption` (`MapWidget.tsx`) : même agrégation
 * (`aggregateCategoryItems`), même réconciliation d'accents avec le GeoJSON
 * (`foldAccents`), mais la sortie n'est plus une `option` ECharts — c'est un
 * modèle de données brut (`{ bubbles, regions }`) que le composant Leaflet
 * transforme en `circleMarker` / `L.geoJSON`.
 */
import type { WidgetConfig } from '../schemas';
import type { WidgetQueryResult, Row } from '../useWidgetData';
import { metricDef, type FieldCatalog } from '../fieldCatalog';
import type { DrillItem } from '../useDrillDown';
import { aggregateCategoryItems } from './categoryItems';

const DEFAULT_LIMIT = 50;
const MAX_BUBBLES = 200;

/** Marques diacritiques (U+0300–U+036F) laissées par la décomposition NFD. */
const COMBINING_MARKS = new RegExp('[\\u0300-\\u036f]', 'g');

/** Compare deux libellés géographiques en ignorant accents et casse. */
function foldAccents(value: string): string {
  return value.normalize('NFD').replace(COMBINING_MARKS, '').toLowerCase();
}

/**
 * Somme des composants `_<numerator>`/`_<denominator>` d'une métrique ratio, groupés
 * par valeur de dimension. Utilisé pour recomposer la métrique taille au niveau cluster
 * (Σnum/Σdénom) — même logique ratio-safe qu'`aggregateMetric`, mais conservant les deux
 * sommes séparément (le cluster additionnera celles de plusieurs villes).
 */
function sumComponentsByKey(
  rows: Row[],
  dimension: string,
  numerator: string,
  denominator: string,
): Map<string, { num: number; denom: number }> {
  const result = new Map<string, { num: number; denom: number }>();
  for (const row of rows) {
    const key = String(row[dimension] ?? '');
    const num = Number(row[`_${numerator}`]);
    const denom = Number(row[`_${denominator}`]);
    const entry = result.get(key) ?? { num: 0, denom: 0 };
    if (Number.isFinite(num)) entry.num += num;
    if (Number.isFinite(denom)) entry.denom += denom;
    result.set(key, entry);
  }
  return result;
}

/** Une ville avec coordonnées (mode bulles). */
export interface BubblePoint {
  name: string;
  lat: number;
  lng: number;
  value: number;
  /** Valeur de la 2e métrique (couleur), agrégée niveau carte uniquement. */
  colorValue?: number;
  /**
   * Composants de la métrique TAILLE (métrique 1), pour recomposer un ratio au niveau
   * cluster (Σnum/Σdénom) sans backend — voir `aggregateClusterValue`. Absents si la
   * métrique est additive (le cluster somme alors `value`) ou en drill.
   */
  sizeNum?: number;
  sizeDenom?: number;
}

/** Un enfant de cluster (bulle individuelle), tel qu'agrégé par `aggregateClusterValue`. */
export interface ClusterChild {
  value: number;
  sizeNum?: number;
  sizeDenom?: number;
}

/**
 * Valeur agrégée d'un cluster de bulles (dézoom carte) : Σnum/Σdénom si tous les enfants
 * portent les composants d'une métrique ratio (recomposition pondérée, comme
 * `aggregateMetric`), sinon Σvalue (métrique additive, ou ratio sans composants — repli).
 */
export function aggregateClusterValue(children: ClusterChild[], isRatio: boolean): number {
  if (isRatio && children.every((c) => c.sizeNum != null && c.sizeDenom != null)) {
    const num = children.reduce((s, c) => s + (c.sizeNum as number), 0);
    const den = children.reduce((s, c) => s + (c.sizeDenom as number), 0);
    return den !== 0 ? num / den : 0;
  }
  return children.reduce((s, c) => s + c.value, 0);
}

/** Une zone du GeoJSON (choroplèthe), nom réconcilié avec les libellés du fond. */
export interface RegionValue {
  name: string;
  value: number;
  /** Valeur de la 2e métrique (couleur), agrégée niveau carte uniquement. */
  colorValue?: number;
}

/** Modèle de couches produit par `buildMapLayers`. */
export interface MapLayers {
  /** true = mode bulles (villes géolocalisées) ; false = mode choroplèthe (régions/pays). */
  isScatter: boolean;
  bubbles: BubblePoint[];
  regions: RegionValue[];
  /** Valeur max des items (avant filtrage bulles), pour calibrer rayon/dégradé côté rendu. */
  max: number;
  /** Max de la 2e métrique (couleur) — absent si pas de 2e métrique configurée ou en drill. */
  colorMax?: number;
  /** Clé de la métrique taille (`config.metrics[0]`), pour la légende. */
  sizeMetric?: string;
  /** Clé de la métrique couleur (`config.metrics[1]`) — absent si pas de 2e métrique / en drill. */
  colorMetric?: string;
}

/**
 * Construit le modèle de couches à partir de la config du widget, des données
 * de requête et (en mode choroplèthe) des noms de zones du GeoJSON cible.
 *
 * `drillItems`, s'il est fourni, remplace l'agrégation des données brutes
 * (niveau courant du drill-down) — même contrat que `buildMapOption`.
 */
export function buildMapLayers(
  config: WidgetConfig,
  data: WidgetQueryResult | null,
  catalog: FieldCatalog | null,
  geoNames: string[] = [],
  drillItems?: DrillItem[] | null,
): MapLayers {
  const metric = config.metrics?.[0];
  const colorMetric = config.metrics?.[1];
  const dimension = config.dimensions?.[0];
  const display = (config.display ?? {}) as Record<string, unknown>;
  const limit = (display.limit as number | undefined) ?? DEFAULT_LIMIT;

  const rawItems: DrillItem[] =
    drillItems ??
    (metric && dimension && data ? aggregateCategoryItems(data.current, dimension, metric, catalog, limit) : []);

  const isScatter = rawItems.some((i) => i._lat != null && i._lng != null);
  const max = rawItems.reduce((m, i) => Math.max(m, i.value), 0);

  // La 2e métrique (couleur) s'agrège depuis les lignes brutes, comme la 1re, mais
  // sans tri/limite : on la joint par clé aux items déjà sélectionnés (limités sur m1).
  // En drill-down (drillItems fourni), le fetch ne porte qu'une métrique → pas de couleur.
  const colorByKey =
    !drillItems && colorMetric && dimension && data
      ? new Map(
          aggregateCategoryItems(data.current, dimension, colorMetric, catalog, Number.MAX_SAFE_INTEGER).map((i) => [
            i.key,
            i.value,
          ]),
        )
      : null;
  const colorMax = colorByKey ? [...colorByKey.values()].reduce((m, v) => Math.max(m, v), 0) : undefined;
  const resolvedColorMetric = colorByKey ? colorMetric : undefined;

  // Composants de la métrique TAILLE (m1), pour recomposer un ratio au niveau cluster
  // sans backend (voir `aggregateClusterValue`) : uniquement si m1 est un ratio ET hors
  // drill (en drill, les composants `_<num>`/`_<denom>` ne sont pas garantis présents).
  const sizeDef = metricDef(catalog, metric);
  const sizeComponentsByKey =
    !drillItems && metric && dimension && data && sizeDef?.type === 'ratio' && sizeDef.numerator && sizeDef.denominator
      ? sumComponentsByKey(data.current, dimension, sizeDef.numerator, sizeDef.denominator)
      : null;

  if (isScatter) {
    const bubbles = rawItems
      .filter((i) => i._lat != null && i._lng != null)
      .slice(0, MAX_BUBBLES)
      .map((i) => ({
        name: i.key,
        lat: i._lat as number,
        lng: i._lng as number,
        value: i.value,
        colorValue: colorByKey?.get(i.key),
        sizeNum: sizeComponentsByKey?.get(i.key)?.num,
        sizeDenom: sizeComponentsByKey?.get(i.key)?.denom,
      }));
    return { isScatter: true, bubbles, regions: [], max, colorMax, sizeMetric: metric, colorMetric: resolvedColorMetric };
  }

  const byFolded = new Map(geoNames.map((n) => [foldAccents(n), n]));
  const regions = rawItems.map((i) => ({
    name: byFolded.get(foldAccents(i.key)) ?? i.key,
    value: i.value,
    colorValue: colorByKey?.get(i.key),
  }));
  return { isScatter: false, bubbles: [], regions, max, colorMax, sizeMetric: metric, colorMetric: resolvedColorMetric };
}

/** Une métrique affichée dans un label de carte. */
export interface LabelMetric {
  label: string;
  value: number;
  format?: (v: number) => string;
}

/**
 * HTML d'un label/tooltip de carte : nom en tête, puis une ligne « label : valeur »
 * par métrique. `escape` : les libellés viennent du catalogue (pas d'entrée libre
 * utilisateur), mais on échappe le nom de zone par prudence (données externes).
 */
export function bubbleLabelHtml(
  name: string,
  metrics: LabelMetric[],
  format: (v: number) => string,
): string {
  const esc = (s: string) =>
    s.replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' })[c] as string);
  const head = `<strong>${esc(name)}</strong>`;
  const lines = metrics.map((m) => `${esc(m.label)} : ${(m.format ?? format)(m.value)}`);
  return [head, ...lines].join('<br>');
}

/** Vue de carte figée, exploitable par Leaflet (`setView`). */
export interface MapView {
  zoom: number;
  center: [number, number]; // [lat, lng]
}

/**
 * Extrait une vue figée valide de `display.map_view`, ou `null`.
 * Défensif : le blob `display` est du JSON persisté, potentiellement partiel/corrompu.
 */
export function parseMapView(display: Record<string, unknown>): MapView | null {
  const v = display.map_view as { zoom?: unknown; center?: unknown } | undefined;
  if (!v) return null;
  const { zoom, center } = v;
  if (typeof zoom !== 'number' || !Number.isFinite(zoom)) return null;
  if (!Array.isArray(center) || center.length !== 2) return null;
  const [lat, lng] = center;
  if (typeof lat !== 'number' || typeof lng !== 'number' || !Number.isFinite(lat) || !Number.isFinite(lng)) {
    return null;
  }
  return { zoom, center: [lat, lng] };
}
